import { useEffect, useState } from "react";
import {
  Box, Stack, Typography, Button, Chip, Alert, CircularProgress,
  IconButton, Tooltip, Snackbar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import RestoreIcon from "@mui/icons-material/Restore";
import {
  listCertificates, rollbackCertificate,
  CertificateRow, CertStatus,
} from "../services/bindCertificates";
import ReplaceCertDialog from "../components/ReplaceCertDialog";

function fmtDate(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

const STATUS_STYLE: Record<CertStatus, [string, string, string]> = {
  vigente:         ["#166534", "#DCFCE7", "Vigente"],
  vence_pronto:    ["#9A6700", "#FFF6E0", "Vence pronto"],
  vencido:         ["#B91C1C", "#FEE2E2", "Vencido"],
  sin_certificado: ["#555",    "#EEE",    "Sin certificado"],
  error:           ["#B91C1C", "#FEE2E2", "Error"],
};

function StatusChip({ status }: { status: CertStatus }) {
  const [fg, bg, label] = STATUS_STYLE[status] ?? ["#333", "#EEE", status];
  return (
    <Chip label={label} size="small"
          sx={{ fontWeight: 700, fontSize: 11, height: 26, borderRadius: 3, color: fg, bgcolor: bg }} />
  );
}

export default function BindCertificates() {
  const [rows,    setRows]    = useState<CertificateRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [err,     setErr]     = useState<string | null>(null);
  const [ok,      setOk]      = useState<string | null>(null);

  const [replaceFor,  setReplaceFor]  = useState<CertificateRow | null>(null);
  const [rollbackFor, setRollbackFor] = useState<CertificateRow | null>(null);
  const [rollingBack, setRollingBack] = useState(false);

  async function load() {
    setLoading(true); setErr(null);
    try {
      setRows(await listCertificates());
    } catch (e: any) {
      setErr(String(e?.message ?? e));
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  async function doRollback() {
    if (!rollbackFor) return;
    setRollingBack(true); setErr(null);
    try {
      const r = await rollbackCertificate(rollbackFor.account);
      setOk(`Rollback OK · ${rollbackFor.secret_name} · vence ${fmtDate(r.summary?.not_after)}`);
      setRollbackFor(null);
      await load();
    } catch (e: any) {
      setErr(String(e?.message ?? e));
    } finally { setRollingBack(false); }
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Certificates</Typography>
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Refresh">
          <span>
            <IconButton onClick={load} disabled={loading}><RefreshIcon /></IconButton>
          </span>
        </Tooltip>
      </Stack>

      {err && <Alert severity="error" onClose={() => setErr(null)} sx={{ mb: 2 }}>{err}</Alert>}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Secret</TableCell>
              <TableCell>CN / Subject</TableCell>
              <TableCell>CUIT</TableCell>
              <TableCell>Usuario API</TableCell>
              <TableCell>Vence</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell>
              </TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  Sin certificados.
                </TableCell>
              </TableRow>
            )}
            {rows.map((r) => (
              <TableRow key={r.secret_name} hover>
                <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{r.account}</TableCell>
                <TableCell>{r.cn ?? "—"}</TableCell>
                <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{r.cuit ?? "—"}</TableCell>
                <TableCell>{r.username ?? "—"}</TableCell>
                <TableCell>{fmtDate(r.not_after)}</TableCell>
                <TableCell><StatusChip status={r.status} /></TableCell>
                <TableCell align="right">
                  <Tooltip title="Reemplazar certificado (.pfx)">
                    <span>
                      <IconButton size="small" onClick={() => setReplaceFor(r)}>
                        <UploadFileIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Rollback al certificado anterior">
                    <span>
                      <IconButton size="small" onClick={() => setRollbackFor(r)}>
                        <RestoreIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <ReplaceCertDialog
        open={!!replaceFor}
        row={replaceFor}
        onClose={() => setReplaceFor(null)}
        onDone={(msg) => { setReplaceFor(null); setOk(msg); load(); }}
      />

      <Dialog open={!!rollbackFor} onClose={rollingBack ? undefined : () => setRollbackFor(null)}>
        <DialogTitle>Rollback de certificado</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Se restaurará la versión anterior (AWSPREVIOUS) del secret{" "}
            <b>{rollbackFor?.secret_name}</b>. ¿Confirmás?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRollbackFor(null)} disabled={rollingBack}>Cancelar</Button>
          <Button onClick={doRollback} variant="contained" color="warning" disabled={rollingBack}>
            {rollingBack ? "Revirtiendo…" : "Confirmar rollback"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!ok}
        autoHideDuration={6000}
        onClose={() => setOk(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setOk(null)} sx={{ width: "100%" }}>{ok}</Alert>
      </Snackbar>
    </Box>
  );
}
